import { AnalyticsApi, baseEndpoint as analyticsApiBaseEndpoint } from './AnalyticsApi';
import { BlogTagApi, baseEndpoint as blogTagApiBaseEndpoint } from './BlogTagApi';
import { CommunityNoteApi, baseEndpoint as communityNoteApiBaseEndpoint } from './CommunityNoteApi';
import { UserDiscountApi, baseEndpoint as userDiscountApiBaseEndpoint } from './UserDiscountApi';
import { TagApi, baseEndpoint as tagBaseEndpoint } from './TagApi';
import { TipApi, baseEndpoint as tipBaseEndpoint } from './TipApi';
import { ThemeApi, baseEndpoint as themeApiBaseEndpoint } from './ThemeApi';
import { TokenApi, baseEndpoint as tokenApiBaseEndpoint } from './TokenApi';
import { InfluencerTokenApi, baseEndpoint as influcenerTokenApiBaseEndpoint } from './InfluencerTokenApi';
import { PageApi, baseEndpoint as pageApiBaseEndpoint } from './PageApi';
import { NotificationApi, baseEndpoint as notificationApiBaseEndpoint } from './NotificationApi';
import { ReferralApi, baseEndpoint as referralApiBaseEndpoint } from './ReferralApi';
import { ProductApi, baseEndpoint as productApiBaseEndpoint } from './ProductApi';
import { PurchaseApi, baseEndpoint as purchaseApiBaseEndpoint } from './PurchaseApi';
import { GroupReferralApi, baseEndpoint as groupReferralApiBaseEndpoint } from './GroupReferralApi';
import { GroupReferralClickApi, baseEndpoint as groupReferralClickApiBaseEndpoint } from './GroupReferralClickApi';
import { StoreCreditRevisionApi, baseEndpoint as storeCreditRevisionApiBaseEndpoint } from './StoreCreditRevisionApi';
import { GroupApi, baseEndpoint as groupApiBaseEndpoint } from './GroupApi';
import { UserApi, baseEndpoint as userApiBaseEndpoint } from './UserApi';
import { BROADCAST_USER_CHANNEL, BroadcastChannelActions, LOCAL_STORAGE_USER_KEY } from '../constants';
import { FeedbackApi, baseEndpoint as feedbackApiBaseEndpoint } from './FeedbackApi';
import { SubscriptionApi, baseEndpoint as subscriptionApiBaseEndpoint } from './SubscriptionApi';
import { ImageApi, baseEndpoint as imageApiBaseEndpoint } from './ImageApi';
import axios from 'axios';
import { BlogPostApi, baseEndpoint as blogPostApiBaseEndpoint } from './BlogPostApi';
import { BlogCommentApi, baseEndpoint as blogCommentApiBaseEndpoint } from './BlogCommentApi';
import { BlogPipelineApi, baseEndpoint as blogPipelineApiBaseEndpoint } from './BlogPipelineApi';
import { BrandImageApi, baseEndpoint as brandImageApiBaseEndpoint } from './BrandImageApi';
import { CoverLetterApi, baseEndpoint as coverLetterApiBaseEndpoint } from './CoverLetterApi';
import { User } from 'referral-types';

export const SITE_URL = import.meta.env.VITE_API_URL;

let analyticsApi: AnalyticsApi;
let blogPipelineApi: BlogPipelineApi;
let brandImageApi: BrandImageApi;
let coverLetterApi: CoverLetterApi;
let blogTagApi: BlogTagApi;
let blogPostApi: BlogPostApi;
let blogCommentApi: BlogCommentApi;
let communityNoteApi: CommunityNoteApi;
let userDiscountApi: UserDiscountApi;
let feedbackApi: FeedbackApi;
let groupApi: GroupApi;
let groupReferralApi: GroupReferralApi;
let groupReferralClickApi: GroupReferralClickApi;
let imageApi: ImageApi;
let influencerTokenApi: InfluencerTokenApi;
let pageApi: PageApi;
let notificationApi: NotificationApi;
let referralApi: ReferralApi;
let productApi: ProductApi;
let purchaseApi: PurchaseApi;
let themeApi: ThemeApi;
let tokenApi: TokenApi;
let tagApi: TagApi;
let tipApi: TipApi;
let storeCreditRevisionApi: StoreCreditRevisionApi;
let subscriptionApi: SubscriptionApi;
let userApi: UserApi;
let refreshAccessTokenPromise: Promise<User> | null = null;

async function refreshSession(tokenApi: TokenApi): Promise<User> {
  if (refreshAccessTokenPromise == null) {
    refreshAccessTokenPromise = (async () => {
      const response = await tokenApi.refresh();
      localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(response));
      return response;
    })().finally(() => {
      refreshAccessTokenPromise = null;
    });
  }

  return await refreshAccessTokenPromise;
}

async function refreshAuthSession(): Promise<User> {
  return await refreshSession(tokenApi);
}

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
}

function isStateChangingMethod(method?: string): boolean {
  if (!method) {
    return false;
  }

  const normalizedMethod = method.toUpperCase();
  return (
    normalizedMethod === 'POST' ||
    normalizedMethod === 'PUT' ||
    normalizedMethod === 'PATCH' ||
    normalizedMethod === 'DELETE'
  );
}

function createClient(baseEndpoint: string, tokenApi?: TokenApi) {
  const config = {
    baseURL: `${SITE_URL}/${baseEndpoint}`,
    withCredentials: true,
  };
  const api = axios.create(config);

  api.interceptors.request.use((request) => {
    if (isStateChangingMethod(request.method)) {
      const csrfToken = getCookieValue('csrfToken');
      if (csrfToken) {
        request.headers = request.headers ?? {};
        request.headers['x-csrf-token'] = csrfToken;
      }
    }

    return request;
  });

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const statusCode = error?.response?.status;
      const isUnauthroized = statusCode === 401;

      if (isUnauthroized) {
        const originalRequest = error.config;
        const hasRetried = Boolean(originalRequest?._retry);

        if (!hasRetried && originalRequest) {
          originalRequest._retry = true;

          try {
            if (tokenApi) {
              await refreshSession(tokenApi);
            }

            return api(originalRequest);
          } catch (e) {
            console.error('Unable to refresh token:', e);
            await logoutUser();
            return Promise.reject(e);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
}

async function logoutUser() {
  localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(BROADCAST_USER_CHANNEL);

    channel.postMessage({
      type: BroadcastChannelActions.UPDATE_USER,
      user: null,
    });
  } else {
    (window as Window).location.href = '/';
  }
}

(function initApis() {
  const tokenApiInstance = createClient(tokenApiBaseEndpoint);

  tokenApi = new TokenApi(tokenApiInstance);

  const analyticsApiInstance = createClient(analyticsApiBaseEndpoint, tokenApi);
  const blogPipelineApiInstance = createClient(blogPipelineApiBaseEndpoint, tokenApi);
  const brandImageApiInstance = createClient(brandImageApiBaseEndpoint, tokenApi);
  const coverLetterApiInstance = createClient(coverLetterApiBaseEndpoint, tokenApi);
  const blogTagApiInstance = createClient(blogTagApiBaseEndpoint, tokenApi);
  const blogPostApiInstance = createClient(blogPostApiBaseEndpoint, tokenApi);
  const blogCommentApiInstance = createClient(blogCommentApiBaseEndpoint, tokenApi);
  const communityNoteApiInstance = createClient(communityNoteApiBaseEndpoint, tokenApi);
  const discountApiInstance = createClient(userDiscountApiBaseEndpoint, tokenApi);
  const pageApiInstance = createClient(pageApiBaseEndpoint, tokenApi);
  const imageApiInstance = createClient(imageApiBaseEndpoint, tokenApi);
  const influencerTokenApiInstance = createClient(influcenerTokenApiBaseEndpoint, tokenApi);
  const notificationApiInstance = createClient(notificationApiBaseEndpoint, tokenApi);
  const referralApiInstance = createClient(referralApiBaseEndpoint, tokenApi);
  const userApiInstance = createClient(userApiBaseEndpoint, tokenApi);
  const feedbackInstance = createClient(feedbackApiBaseEndpoint, tokenApi);
  const groupApiInstance = createClient(groupApiBaseEndpoint, tokenApi);
  const groupReferralApiInstance = createClient(groupReferralApiBaseEndpoint, tokenApi);
  const groupReferralClickApiInstance = createClient(groupReferralClickApiBaseEndpoint, tokenApi);
  const themeApiInstance = createClient(themeApiBaseEndpoint, tokenApi);
  const tagApiInstance = createClient(tagBaseEndpoint, tokenApi);
  const tipApiInstance = createClient(tipBaseEndpoint, tokenApi);
  const productApiInstance = createClient(productApiBaseEndpoint, tokenApi);
  const purchaseApiInstance = createClient(purchaseApiBaseEndpoint, tokenApi);
  const storeCreditRevisionApiInstance = createClient(storeCreditRevisionApiBaseEndpoint, tokenApi);
  const subscriptionApiInstance = createClient(subscriptionApiBaseEndpoint, tokenApi);

  analyticsApi = new AnalyticsApi(analyticsApiInstance);
  blogPipelineApi = new BlogPipelineApi(blogPipelineApiInstance);
  brandImageApi = new BrandImageApi(brandImageApiInstance);
  coverLetterApi = new CoverLetterApi(coverLetterApiInstance);
  blogTagApi = new BlogTagApi(blogTagApiInstance);
  blogPostApi = new BlogPostApi(blogPostApiInstance);
  blogCommentApi = new BlogCommentApi(blogCommentApiInstance);
  communityNoteApi = new CommunityNoteApi(communityNoteApiInstance);
  userDiscountApi = new UserDiscountApi(discountApiInstance);
  pageApi = new PageApi(pageApiInstance);
  imageApi = new ImageApi(imageApiInstance);
  influencerTokenApi = new InfluencerTokenApi(influencerTokenApiInstance);
  notificationApi = new NotificationApi(notificationApiInstance);
  referralApi = new ReferralApi(referralApiInstance);
  feedbackApi = new FeedbackApi(feedbackInstance);
  userApi = new UserApi(userApiInstance);
  groupApi = new GroupApi(groupApiInstance);
  groupReferralApi = new GroupReferralApi(groupReferralApiInstance);
  groupReferralClickApi = new GroupReferralClickApi(groupReferralClickApiInstance);
  tagApi = new TagApi(tagApiInstance);
  tipApi = new TipApi(tipApiInstance);
  themeApi = new ThemeApi(themeApiInstance);
  productApi = new ProductApi(productApiInstance);
  purchaseApi = new PurchaseApi(purchaseApiInstance);
  storeCreditRevisionApi = new StoreCreditRevisionApi(storeCreditRevisionApiInstance);
  subscriptionApi = new SubscriptionApi(subscriptionApiInstance);
})();

export {
  analyticsApi,
  blogPipelineApi,
  brandImageApi,
  coverLetterApi,
  blogTagApi,
  blogPostApi,
  blogCommentApi,
  tokenApi,
  userDiscountApi,
  feedbackApi,
  pageApi,
  imageApi,
  influencerTokenApi,
  referralApi,
  groupApi,
  userApi,
  communityNoteApi,
  notificationApi,
  tagApi,
  tipApi,
  groupReferralApi,
  groupReferralClickApi,
  themeApi,
  productApi,
  purchaseApi,
  storeCreditRevisionApi,
  subscriptionApi,
  refreshAuthSession,
};
